/* env browser */
import { CustomError, Utilities } from '../common';
import { shortNameMapping } from '../constants';
import { resolveAllParameter, resolveParameter, resolveSpecialParameter } from '../common/Parameter';
// ------------------ private variables ----------------

const nativeAppToken = new Date().getTime().toString(16);

// ------------------ private methods ------------------

/**
 * Creates a communication bridge to the native app whn sensor is running in a webview. First it's checks
 * if the sensor is running in an capable webview. When checks is true it will set hybrid flag of the
 * sensor state to true and it will save the platform also to the state. It will be later transfered to
 * relay as technical information
 *
 * @param {*} parameters - Initialization parameters for the current sensor instance
 * @param {Sensor} sensor - Current sensor instance
 */
const createHybridBridge = (parameters, sensor) => {
  const { basicSettings, state } = sensor;
  const hybridPlatform = Utilities.checkIsWebView(basicSettings.webviewDetection);
  if (hybridPlatform !== '') {
    state.hybrid.detected = hybridPlatform !== '';
    state.hybrid.platform = hybridPlatform;
    state.hybrid.parameters = parameters;
    if (hybridPlatform === 'ios') {
      const message = JSON.stringify({
        type: 'request',
        payload: {
          globalObject: window.IMAGlobalObject,
          command: 'hybrid',
          token: nativeAppToken,
        },
      });
      window.webkit.messageHandlers[basicSettings.webviewDetection.ios].postMessage(message);
    }
    if (hybridPlatform === 'android') {
      window[basicSettings.webviewDetection.android].getNativeAppData(window.IMAGlobalObject, 'hybrid', nativeAppToken);
    }
  }
};

/**
 * Secure message processing. It will serialize the given data to JSON, creates a random secret, encrypts the serialized data with this secret
 * and generates a checksum from serialized data. It will recommend to use GET as request method.
 *
 * @param {Sensor} sensor - The current sensor instance
 * @param {*} data - The data to encrypt
 * @return {Promise} - Resolves with a collection containing the encrypted payload, encrypted secret, checksum and the desired request method
 */
const secure = (sensor, data) => new Promise((resolve, reject) => {
  try {
    const { crypto, logger, systemConfig } = sensor;
    const serializedData = JSON.stringify(data);
    // Create a random secret
    const secret = crypto.createSecret();
    // Create promises for the encryption process and execute them later parallel
    const promises = [
      // Encrypt payload with AES and a shared random secret
      crypto.encryptAesCbc(serializedData, secret),
      // Encrypt shared secret with RSA and a public key
      crypto.encryptRsa(secret, systemConfig.publicRSA),
      // Create a checksum from the payload
      crypto.hash(serializedData),
    ];
    // Log serialized payload to browser console
    logger.debug('Serialized payload (JSON): ', serializedData);
    // Log shared secret to browser console
    logger.debug('Shared secret (Base64): ', secret);
    Promise
      .all(promises)
      .then((results) => {
        // Log some information and add time complexity for processing
        logger.debug('Serialized payload (AES encrypted):', results[0]);
        logger.debug('Secret (RSA encrypted): ', results[1]);
        logger.debug('Checksum (SHA-1): ', results[2]);
        resolve([...results, 'GET']);
      })
      .catch(error => reject(error));
  } catch (error) {
    // Reject promise with errors which are out of encryption scope.
    // For example errors, which occurred while serializing the data to JSON
    reject(error);
  }
});
/**
 * Loads the configuration form storage or form relay when config ttl mismatches or config could not be parsed
 *
 * @param {Sensor} sensor - Current sensor instance
 * @param {Object} initParameters - Dictionary of init parameters
 * @returns {Promise} Resolves with the sensor configuration or rejects with error
 */
const loadConfig = (sensor, initParameters) => new Promise((resolve, reject) => {
  try {
    const { store, relay, logger } = sensor;
    let siteHash; // memoize site hash
    logger.debug('Generating site hash for site:', initParameters.site);
    sensor.crypto
      .hash(initParameters.site)
      .then((hash) => {
        siteHash = hash;
        logger.debug('Generating of site hash', siteHash, ' for site:', initParameters.site, 'finished.');
        return store
          .readConfig(siteHash)
          .then(config => resolve({ systemConfig: config, siteHash }))
          .catch(() => relay.fetchConfig(initParameters))
          .then(config => resolve({ systemConfig: config, siteHash }))
          .catch(error => reject(error));
      })
      .catch(error => reject(error));
  } catch (error) {
    reject(error);
  }
});

/**
 * None secure message processing. It will serialize the data to JSON and creates a checksum from it. It will recommend to use POST as request method,
 * because the data cannot be serialized to url query parameters
 *
 * @param {Sensor} sensor - The current sensor instance
 * @param {*} data - The data to serialize
 * @return {Promise}
 */
const noneSecure = (sensor, data) => new Promise((resolve, reject) => {
  try {
    const { crypto, logger } = sensor;
    // Serialize payload to JSON
    const serializedData = JSON.stringify(data);
    // Log serialized payload to browser console
    logger.debug('Serialized payload (JSON): ', serializedData);
    // Create a checksum
    crypto
      .hash(serializedData)
      .then((checksum) => {
        resolve([data, undefined, checksum, 'POST']);
      })
      .catch(error => reject(error));
  } catch (error) {
    // Reject promise with errors which are out of encryption scope.
    // For example errors, which occurred while serializing the data to JSON
    reject(error);
  }
});

/**
 * Starts the sensor event processing and transmit the event to the relay. It will only start the message sending when the current sensor is already
 * initialized, the privacy setting of the current browser allowing the tracking and the transmitted event is valid for messaging.
 *
 * It will try to use the secure message processing when secure mode is activated (which it is by default). It will fallback automatically to a none
 * secure message processing when secure mode is deactivate or an error occurred while encrypting the data. It resolves with the response from the
 * relay or an error when none secure message processing fails (its the fallback which is called at least).
 *
 * @param {Sensor} sensor - Current sensor instance
 * @param {String} [sensorEvent] - Optional sensor event
 * @param {Number} [startTime] - Optional start time for this process
 * @param {String} [timeCode] - Optional time code for time complexity metrics
 * @return {Promise} Resolves with the response form the relay or an error when none secure message processing fails
 */
const sendEvent = (sensor, sensorEvent = '', startTime = new Date().getTime(), timeCode = 'T4') => new Promise((resolve) => {
  const {
    clrData,
    logger,
    privacySettings,
    relay,
    state,
    basicSettings,
    systemConfig,
  } = sensor;
  if (state.inited
    && Utilities.isDoNotTrack(sensor) === false
    && state.globalParams.privacySetting !== privacySettings.stop
    && (!basicSettings.checkEvents || (basicSettings.checkEvents && basicSettings.eventList.indexOf(sensorEvent) > -1))
  ) {
    // Transmit sensor event to clrData model
    clrData.setEvent(sensorEvent);
    // Request calculated sensor data from the clr data model
    const data = clrData.getData();
    // deleting profile by default
    delete data[shortNameMapping.profile];

    logger.debug('Payload plain: ', data);
    // Check if secure mode is activated
    if (systemConfig.secureMode === true) {
      // Start secure message processing
      secure(sensor, data)
        .then(([payload, secret, checksum, method]) => {
          // Save time complexity for this process before sending data to relay
          state.runtimes += `${timeCode}:${new Date().getTime() - startTime}`;
          // Transmit data to relay
          return relay.transmitData(payload, secret, checksum, method);
        })
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          // When something broke on message encryption we have to try fallback to none secure message processing
          // Log the occurred error to current state
          state.scriptErrors.push(new CustomError('E11', error.message));
          // Overwrite secure mode to signal the relay that the incoming data is not encrypted
          systemConfig.secureMode = false;
          // Start over in none secure mode
          sendEvent(sensor, sensorEvent, startTime, timeCode)
            .then((response) => {
              // Resolve with response
              resolve(response);
            });
        });
    } else {
      // Fallback to none secure message processing
      noneSecure(sensor, data)
        .then(([payload, , checksum, method]) => {
          // Save time complexity for this process before sending data to relay
          state.runtimes += `${timeCode}:${new Date().getTime() - startTime}`;
          return relay.transmitData(payload, undefined, checksum, method);
        })
        .then((response) => {
          resolve(response);
        })
        .catch(error => resolve(error));
    }
  } else {
    logger.debug('Sensor is not initialize or his privacy setting don\'t allow the tracking or the transmitted event is not valid.');
  }
});
/**
 * Terminates the sensor and when lso usage is activated the current state will be written persistent to the local storage
 *
 * @param {Sensor} sensor - Current sensor instance
 */
const terminate = sensor => new Promise((resolve) => {
  const {
    clsData,
    state,
    store,
    systemConfig,
  } = sensor;
  // Check if local storage should be used to persistent measured data
  if (state.persistence) {
    // Update CLS data model (will be written to local storage)
    clsData
      .update()
      // Save cls data to local storage
      .then(() => store.save())
      // Save sensor configuration locally
      .then(() => store.saveConfig(systemConfig))
      .then(() => {
        state.scriptErrors = [];
        return true;
      })
      // Resolve promise
      .then(() => resolve());
  }
  // Resolve promise immediately when persistent of measured data is not necessary
  resolve();
});

/**
 * Starts calculation by requesting panel data and categorization from the
 * relay server and then inject this information into the data calculation
 * process.
 *
 * @param {Sensor} sensor
 */
const startCalculation = sensor => new Promise((resolve) => {
  const {
    state,
    clrData,
    logger,
  } = sensor;
  // Start calculation of data
  clrData.calculateData();
  // Start message processing
  return Promise.all([
    sendEvent(sensor, state.globalEvent),
    terminate(sensor),
  ])
    .then((results) => {
      if (results[0] instanceof Error) {
        // Log error
        logger.debug('Error on event sending: ', results[0].message);
      } else {
        // Log relay response
        logger.debug('Response from relay: ', results[0]);
      }
      logger.debug('Sensor completed work.');
      resolve(true);
    });
});

/**
 * Integrates and activates the sentry error reporting client
 *
 * @param {Sensor} sensor - Current sensor instance
 * @return {Promise<Undefined>}
 */
const activateSentry = sensor => new Promise((resolve) => {
  const { logger, state, systemConfig } = sensor;
  const { dsn, script, integrity } = systemConfig.sentry;
  Utilities
    .createScriptTag(script, integrity)
    .then(() => {
      if (window.Sentry) {
        window.Sentry.init({ dsn });
        return Promise.resolve();
      }
      return Promise.reject(new CustomError('E14', 'Sentry was activated but could not be loaded.'));
    })
    .then(() => {
      logger.debug('Sentry successful activated on host:', window.location.host);
      resolve();
    })
    .catch((error) => {
      logger.debug('Sentry failed to activate on host', window.location.host, 'with error:', error);
      state.scriptErrors.push(error);
      resolve();
    });
});

/**
 * Parses the parameters of type JSON to an js object or clones a given js object deeply.
 *
 * @param {JSON|Object} parameters - Parameters to be parsed
 * @return {Object} Deeply cloned or parsed js object
 */
const parseParameters = (parameters) => {
  if (Utilities.isJSON(parameters)) {
    return JSON.parse(parameters);
  }
  return JSON.parse(JSON.stringify(parameters));
};
// ------------------ public class and methods ------------------
/**
 * @class Sensor
 */
export default class Sensor {
  /**
   * Initializes a new instance of the acsam sensor
   *
   * @param {Object} options - Sensor options
   * @param {Object} options.basicSettings - The basic settings for the sensor instance
   * @param {Object} options.defaultState - The default state for the sensor instance
   * @param {Object} options.privacySettings - The default state for the sensor instance
   * @param {Object} options.systemConfig - THe
   * @param {Object} logger - The logging instance used by the sensor
   * @param {Object} crypto - The crypto instance used by the sensor
   * @param {Object} dependencies - A collection of dependencies used by the sensor
   * @param {ClrData} dependencies.ClrData - The CLR data model class
   * @param {ClsData} dependencies.ClsData - The CLS data model class
   * @param {Http} dependencies.Http - The http interface
   * @param {Relay} dependencies.Relay - The relay class
   * @param {Store} dependencies.Store - The store class
   */
  constructor(options, logger, crypto, dependencies) {
    // Extract the important basic configurations from the option object
    const {
      basicSettings,
      defaultState,
      privacySettings,
    } = options;
    const {
      ClrData,
      ClsData,
      Http,
      Relay,
      Store,
    } = dependencies;
    this.name = 'SensorClass';
    // Set basic settings
    this.basicSettings = basicSettings;
    // Set default state
    this.state = defaultState();
    // Set privacy settings
    this.privacySettings = privacySettings;
    // initialize a new instance of ClrData model
    this.clrData = new ClrData(this);
    // Initialize a new instance of ClsData model
    this.clsData = new ClsData(this);
    // Initialize a new instance of Store (local storage)
    this.store = new Store(this);
    // Initialize a new instance of Relay service
    this.relay = new Relay(this, Http);
    // Store logger instance on local sensor to make it available in sensor instance
    this.logger = logger;
    // Store crypto engine as instance in sensor
    this.crypto = crypto;
  }

  /**
   * Initializes and configures the main functionality of the sensor by mapping
   * all provide parameters to the configuration and loading persistent
   * information from the local storage
   *
   * @param {Object} parameters - Parameters for the sensor functionality
   * @param {Number} [startTime] - Optional start time for this process
   * @param {String} [timeCode] - Optional time code for the time complexity metrics
   * @param {Boolean} [hybridMode = false] - Optional hybrid check (on false by default)
   * @return {Promise}
   */
  init(parameters, hybridMode = false, startTime = new Date().getTime(), timeCode = 'T0') {
    return new Promise((resolve) => {
      this.state.runtimes = '';
      this.state.startTime = startTime;
      const parsedParams = parseParameters(parameters);
      // Check and process parsed parameters
      const { initParameter, stopSensorInit } = resolveAllParameter(parsedParams, this.logger);
      if (stopSensorInit === false) {
        if (hybridMode) {
          createHybridBridge(initParameter, this);
        }
        // Extract hostname from provided amp canonical url or from current location object
        const hostname = initParameter.amp === true ? Utilities.getUrlHost(initParameter.ampUrl) : document.location.hostname;
        if (Utilities.checkIsFirstPartyContext(this, initParameter.serviceDomainName, hostname) === true) {
          this.logger.debug('Loading sensor configuration');
          loadConfig(this, initParameter, startTime, timeCode)
            .then(({ systemConfig, siteHash }) => {
              this.logger.debug('Loading sensor configuration finished');
              this.systemConfig = systemConfig;
              const specialParameters = resolveSpecialParameter(parsedParams, systemConfig, this.logger);
              // Extract location from the provided amp url or take current location object
              const location = initParameter.amp === true ? Utilities.getLocation(initParameter.ampUrl) : window.location;
              // Check if current web sensor is executed in a valid environment
              if (hybridMode === false) {
                if (Utilities.checkValidEnvironments(this, window.navigator.userAgent, location) === true) {
                  // transfer parsed parameters to the state
                  this.state.globalParams = { ...initParameter, ...specialParameters };
                  // Add site hashing to globalParams
                  this.state.globalParams.siteHash = siteHash;
                  // Create promise collection
                  const promises = [];
                  // Check if privacy settings are in extended mode (most protection)
                  if (this.state.globalParams.privacySetting === this.privacySettings.extended) {
                    const message = 'Extended privacy enabled by current browser.';
                    this.logger.debug(message);
                    this.state.scriptErrors.push(new CustomError('N0', message));
                  }
                  // Check if sentry is enabled and load it
                  if (this.state.globalParams.sentry === true && this.systemConfig.sentry.enabled) {
                    // Push sentry activation to promise map
                    promises.push(activateSentry(this));
                  }
                  this.logger.debug('Initialize sensor.');
                  // Hash site property for local storage and activate sentry when necessary vi promise map.
                  Promise.all(promises)
                    .then(() => this.store.read())
                    .then(() => {
                    // Overwrite cks
                      if (this.state.hybrid.detected && this.state.hybrid.nativeAppData) {
                        this.clsData.data.site.lastTransaction = this.state.hybrid.nativeAppData.lastTransaction;
                        this.clsData.data.site.eventTime = this.state.hybrid.nativeAppData.lastEventTime;
                      }
                      // Set initialized state on true
                      this.state.inited = true;
                      // Save time complexity for this process in state run times
                      this.state.runtimes = `${timeCode}:${new Date().getTime() - startTime}`;
                      this.logger.debug('Initialize completed.');
                      resolve();
                    });
                } else {
                  this.logger.debug('Url or User Agent not allowed.');
                  resolve();
                }
              } else {
                resolve();
              }
            })
            .catch((error) => {
              this.logger.debug('Error while loading sensor config:', error);
              resolve();
            });
        } else {
          this.logger.debug('Outside of first-party content.');
          resolve();
        }
      } else {
        this.logger.debug('!!!SENSOR INIT STOPPED!!! invalid init parameter !!!NO MEASURES ARE RECORDED!!!');
        console.error('[IMA] !!!SENSOR INIT STOPPED!!! invalid init parameter !!!NO MEASURES ARE RECORDED!!!');
        resolve();
      }
    });
  }

  /**
   * Initializes the sensor with the native app data. This method is called by the native app when
   * the hybrid bridge is initialized before.
   *
   * @param {Object} parameters - Hybrid parameters
   * @param {Object|String} nativeAppData - Native app data
   * @param {String} token - The token which was send to hybrid bridge
   * @return {Promise|void} Promise from the init function
   */
  hybrid(parameters, nativeAppData, token) {
    if (token === nativeAppToken) {
      const givenParams = parseParameters(parameters);
      const nativeAppSite = resolveParameter(givenParams, 'site', this.logger);
      const nativeAppCode = resolveParameter(givenParams, 'code', this.logger);
      const nativeServiceDomainName = resolveParameter(givenParams, 'serviceDomainName', this.logger);
      const hybridCodePrefix = nativeAppSite === this.state.hybrid.parameters.site ? 'hyb2' : 'hyb';
      // Overwrite code with hybrid notated code
      givenParams.code = `___${hybridCodePrefix}___${this.state.hybrid.parameters.site}___${nativeAppCode}`;
      // Cache native app data for later processing
      this.state.hybrid.nativeAppData = Utilities.isJSON(nativeAppData) ? JSON.parse(nativeAppData) : nativeAppData;
      // Take parameters from the initialization which happened before as web parameters
      const webParameters = {
        ...this.state.hybrid.parameters,
      };
      // Take the submitted parameters as app parameters with hybrid flagged code
      const appParameters = {
        site: nativeAppSite,
        code: givenParams.code,
        serviceDomainName: nativeServiceDomainName,
      };
      // Call initialization with merged web and app parameters
      return this
        .init({ ...webParameters, ...appParameters }, false, this.state.startTime, undefined)
        .then(() => this.count(false));
    }
    return this.logger.debug('hybrid method was not called from a native app instance! Execution aborted.');
  }

  /**
   * Starts the processing of an sensor event specified by a provided event name
   *
   * @param {String} eventName - Name of the event. For supported events take a look into ../constant/systemConfig.js#eventList
   * @return {Promise} Returns a promise which always resolves
   */
  event(eventName) {
    return new Promise((resolve) => {
      if (this.state.inited === false) {
        this.logger.debug('IMA sensor not initialized! First acsam(init, {...parameters})!');
        resolve();
      } else {
        // Reset runtimes to init runtime
        // eslint-disable-next-line prefer-destructuring
        const initRuntime = /^T0:[0-9]{0,4}/.exec(this.state.runtimes);
        this.state.runtimes = initRuntime ? initRuntime[0] : '';
        // Create new data set for relay client
        this.clrData.create();
        this.state.globalEvent = eventName;
        if (!this.state.globalParams.eventHandling
          || (this.state.globalParams.eventHandling && this.basicSettings.indexOf(this.state.globalEvent) > -1)
        ) {
          this.state.persistence = false;
        }
        startCalculation(this)
          .then(() => resolve());
      }
    });
  }

  /**
   * Starts the normal page view count process
   *
   * @param {Boolean} hybridMode - Communicates first with app sdk before starting any measurement
   * @return {Promise} Returns a promise which always resolves
   */
  count(hybridMode = false) {
    return new Promise((resolve) => {
      if (hybridMode === true && this.state.hybrid.detected === true) {
        createHybridBridge(this.state.hybrid.parameters, this);
        resolve();
      } else if (hybridMode === true && this.state.hybrid.detected === false) {
        this.logger.debug('Web sensor not properly initialized in hybrid mode');
        resolve();
      } else {
        const start = new Date().getTime();
        if (this.state.inited) {
          // Reset runtimes to init runtime
          // eslint-disable-next-line prefer-destructuring
          const initRuntime = /^T0:[0-9]{0,4}/.exec(this.state.runtimes);
          this.state.runtimes = initRuntime ? initRuntime[0] : '';
          // Create new data set for relay client
          this.clrData.create();
          // Start calculation
          startCalculation(this).then(() => {
            const end = new Date().getTime() - start;
            this.logger.debug('Metric calculation completed in', end, 'ms.');
            resolve();
          });
        } else {
          this.logger.debug('Not initialized.');
          resolve();
        }
      }
    });
  }
}
